import { planets, dayRulers } from "../data/planetaryData";

export interface PlanetaryHour {
  planet: string;
  symbol: string;
  startTime: Date;
  endTime: Date;
  nextPlanet: string;
  nextSymbol: string;
}

// Cached sequence of planets for each day of the week
const planetarySequenceCache: Record<number, Array<(typeof planets)[0]>> = {};

// Helper function to get the planetary sequence for a specific day
const getPlanetarySequence = (
  dayOfWeek: number
): Array<(typeof planets)[0]> => {
  // Return from cache if already calculated
  if (planetarySequenceCache[dayOfWeek]) {
    return planetarySequenceCache[dayOfWeek];
  }

  // Get the ruling planet for the day
  const dayRuler = dayRulers[dayOfWeek as keyof typeof dayRulers];

  // Find the index of the day ruler in the planets array
  let startIndex = planets.findIndex((p) => p.name === dayRuler);
  if (startIndex === -1) startIndex = 0;

  // Create the sequence for the day - preallocate the array size for better performance
  const sequence = new Array(24);
  let currentIndex = startIndex;

  // Generate 24 hours worth of planets
  for (let i = 0; i < 24; i++) {
    sequence[i] = planets[currentIndex];
    currentIndex = (currentIndex + 1) % 7;
  }

  // Store in cache
  planetarySequenceCache[dayOfWeek] = sequence;

  return sequence;
};

// Cache of daily planetary hours calculations to avoid repeated calculations
const planetaryHoursCache = new Map<string, PlanetaryHour[]>();

// Generate a cache key based on sunrise and sunset
const generateCacheKey = (sunrise: Date, sunset: Date): string => {
  return `${sunrise.toISOString()}-${sunset.toISOString()}`;
};

export const calculatePlanetaryHour = (
  sunrise: Date,
  sunset: Date,
  referenceDate: Date = new Date()
): PlanetaryHour | null => {
  // Try to use precalculated hours to find the current one
  const cacheKey = generateCacheKey(sunrise, sunset);
  const cachedHours = planetaryHoursCache.get(cacheKey);

  if (cachedHours) {
    // Find the hour that contains the reference date
    const now = referenceDate.getTime();
    for (const hour of cachedHours) {
      if (now >= hour.startTime.getTime() && now < hour.endTime.getTime()) {
        return hour;
      }
    }
  }

  const now = referenceDate;

  // Get the day of the week for the sunrise date
  const sunriseDayOfWeek = sunrise.getDay();

  // Create reference dates - minimize creating new Date objects
  const tomorrowSunriseTime = sunrise.getTime() + 24 * 60 * 60 * 1000;
  const yesterdaySunsetTime = sunset.getTime() - 24 * 60 * 60 * 1000;

  // Determine if it's day, night after sunset, or night before sunrise
  const nowTime = now.getTime();
  const sunriseTime = sunrise.getTime();
  const sunsetTime = sunset.getTime();

  let isDay = false;
  let isAfterMidnightBeforeSunrise = false;

  if (nowTime >= sunriseTime && nowTime < sunsetTime) {
    isDay = true;
  } else if (nowTime >= sunsetTime) {
    isDay = false;
    isAfterMidnightBeforeSunrise = false;
  } else if (nowTime < sunriseTime) {
    isDay = false;
    isAfterMidnightBeforeSunrise = true;
  }

  // Calculate hour duration and determine the correct period start time
  let hourDuration: number;
  let periodStartTime: number;
  let dayOfWeekForPlanets: number;

  if (isDay) {
    // Day period (sunrise to sunset)
    hourDuration = (sunsetTime - sunriseTime) / 12;
    periodStartTime = sunriseTime;
    dayOfWeekForPlanets = sunriseDayOfWeek;
  } else if (isAfterMidnightBeforeSunrise) {
    // Night period (yesterday's sunset to today's sunrise)
    const yesterdaySunriseDayOfWeek = (sunriseDayOfWeek - 1 + 7) % 7; // Safer way to get previous day
    dayOfWeekForPlanets = yesterdaySunriseDayOfWeek;

    hourDuration = (sunriseTime - yesterdaySunsetTime) / 12;
    periodStartTime = yesterdaySunsetTime;
  } else {
    // Night period (today's sunset to tomorrow's sunrise)
    hourDuration = (tomorrowSunriseTime - sunsetTime) / 12;
    periodStartTime = sunsetTime;
    dayOfWeekForPlanets = sunriseDayOfWeek;
  }

  // Get the sequence of planetary hours for this day
  const daySequence = getPlanetarySequence(dayOfWeekForPlanets);

  // Find which hour of the period we're in
  const timeSincePeriodStart = nowTime - periodStartTime;
  const hourIndex = Math.floor(timeSincePeriodStart / hourDuration);

  // Calculate hour index in the sequence
  let sequenceIndex: number;

  if (isDay) {
    sequenceIndex = hourIndex; // 0-11 for day hours
  } else {
    sequenceIndex = 12 + hourIndex; // 12+ for night hours
  }

  // Return current planetary hour and next planet
  if (sequenceIndex >= 0 && sequenceIndex < 24) {
    const nextSequenceIndex = (sequenceIndex + 1) % 24;
    const nextPlanet = daySequence[nextSequenceIndex];

    // Create Date objects only at the end when needed
    const hourStartTime = new Date(periodStartTime + hourIndex * hourDuration);
    const hourEndTime = new Date(
      periodStartTime + (hourIndex + 1) * hourDuration
    );

    return {
      planet: daySequence[sequenceIndex].name,
      symbol: daySequence[sequenceIndex].symbol,
      startTime: hourStartTime,
      endTime: hourEndTime,
      nextPlanet: nextPlanet.name,
      nextSymbol: nextPlanet.symbol,
    };
  }

  return null;
};

export const calculateAllPlanetaryHours = (
  sunrise: Date,
  sunset: Date,
  referenceDate: Date = new Date()
): PlanetaryHour[] => {
  // Check for cached results first
  const cacheKey = generateCacheKey(sunrise, sunset);
  const cachedResult = planetaryHoursCache.get(cacheKey);
  if (cachedResult) {
    return cachedResult;
  }

  const result: PlanetaryHour[] = [];

  // Get sunrise day of week
  const sunriseDayOfWeek = sunrise.getDay();

  // Use timestamps instead of Date objects for calculations
  const sunriseTime = sunrise.getTime();
  const sunsetTime = sunset.getTime();
  const tomorrowSunriseTime = sunriseTime + 24 * 60 * 60 * 1000;

  // Day hours (sunrise to sunset)
  const dayHourDuration = (sunsetTime - sunriseTime) / 12;
  // Night hours (sunset to next sunrise)
  const nightHourDuration = (tomorrowSunriseTime - sunsetTime) / 12;

  // Get the sequence of planetary hours for this day
  const daySequence = getPlanetarySequence(sunriseDayOfWeek);

  // Preallocate the array size for better performance
  result.length = 24;

  // Calculate day hours (sunrise to sunset)
  for (let i = 0; i < 12; i++) {
    const startTime = new Date(sunriseTime + i * dayHourDuration);
    const endTime = new Date(sunriseTime + (i + 1) * dayHourDuration);
    const nextPlanetIndex = (i + 1) % 24;

    result[i] = {
      planet: daySequence[i].name,
      symbol: daySequence[i].symbol,
      startTime,
      endTime,
      nextPlanet: daySequence[nextPlanetIndex].name,
      nextSymbol: daySequence[nextPlanetIndex].symbol,
    };
  }

  // Calculate night hours (sunset to next sunrise)
  for (let i = 0; i < 12; i++) {
    const startTime = new Date(sunsetTime + i * nightHourDuration);
    const endTime = new Date(sunsetTime + (i + 1) * nightHourDuration);
    const sequenceIndex = 12 + i;
    const nextPlanetIndex = (sequenceIndex + 1) % 24;

    result[12 + i] = {
      planet: daySequence[sequenceIndex].name,
      symbol: daySequence[sequenceIndex].symbol,
      startTime,
      endTime,
      nextPlanet: daySequence[nextPlanetIndex].name,
      nextSymbol: daySequence[nextPlanetIndex].symbol,
    };
  }

  // Cache the results
  planetaryHoursCache.set(cacheKey, result);

  // Limit cache size to prevent memory issues
  if (planetaryHoursCache.size > 20) {
    // Delete the oldest entry - ensure the key is not undefined
    const keysIterator = planetaryHoursCache.keys();
    const firstKey = keysIterator.next().value;
    if (firstKey) {
      planetaryHoursCache.delete(firstKey);
    }
  }

  return result;
};
